import { AttachmentType } from "@/modules/beneficiary/common/enum";
import type { IBeneficiaryItem } from "@/modules/beneficiary/common/types";
import {
  useDeleteBeneficiaryAccountMutation,
  useDeleteBeneficiaryCardMutation,
} from "@/modules/beneficiary/services";
import type { IError } from "@/shared/common/types";
import { useNotify } from "@/shared/hooks";
import { Modal } from "@ab.uitools/ui-library/components/Modal";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { useTranslation } from "react-i18next";

interface Props {
  isOpen: boolean;
  isLast: boolean;
  beneficiaryId: string;
  item: (IBeneficiaryItem & { id: string }) | null;
  type: AttachmentType;
  onClose: () => void;
  onSuccess: () => void;
}

export const DeleteAccountCardModal = ({
  isOpen,
  item,
  type,
  isLast,
  onClose,
  onSuccess,
  beneficiaryId,
}: Props) => {
  const { t } = useTranslation();
  const { showErrorSnackbar, showSuccessSnackbar } = useNotify();
  const [deleteAccount, { isLoading: isDeletingAccount }] = useDeleteBeneficiaryAccountMutation();
  const [deleteCard, { isLoading: isDeletingCard }] = useDeleteBeneficiaryCardMutation();

  const isLoading = isDeletingAccount || isDeletingCard;

  const handleDelete = () => {
    if (!item) return;

    const isAccount = type === AttachmentType.Account;

    const request =
      isAccount
        ? deleteAccount({ beneficiaryId, beneficiaryEntryId: item.id })
        : deleteCard({ beneficiaryId, beneficiaryEntryId: item.id });

    request
      .unwrap()
      .then(() => {
        const key = isLast ?
          "beneficiaryHasBeenSuccessfullyDeleted" :
          isAccount ? "beneficiaryAccountItemHasBeenSuccessfullyDeleted" :
            "beneficiaryCardItemHasBeenSuccessfullyDeleted";
        showSuccessSnackbar(t(`alerts.${key}`));
        onSuccess();
        onClose();
      })
      .catch((err: IError) => {
        showErrorSnackbar(err);
        onClose();
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleDelete}
      buttonProps={{
        confirm: {
          buttonText: t("general.delete"),
          type: "danger",
          isLoading,
          disabled: isLoading,
        },
        cancel: { buttonText: t("general.cancel") },
      }}
      titleProps={{
        title: t("beneficiary.deleteItemModal.title"),
        size: "large",
      }}
    >
      <Text>
        {t(
          isLast ?
            "beneficiary.deleteLastItemModal.description" :
            "beneficiary.deleteItemModal.description",
          { count: item?.number ?? "" }
        )}
      </Text>
    </Modal>
  );
};
